import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CustomerHeader from "../components/CustomerHeader";
import CustomerFooter from "../components/CustomerFooter";
import ComingSoonPage from "../components/ComingSoonPage";
import { useGooglePlacesAutocomplete } from "../hooks/useGooglePlacesAutocomplete";
import "../App.css";

const MOBILE_ITEMS_PER_PAGE = 30;

function FindFreelancers() {
  const navigate = useNavigate();
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [freelancers, setFreelancers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState("all");
  const [radiusOptions, setRadiusOptions] = useState([5, 10, 25, 50]);
  const [sortBy, setSortBy] = useState("name");
  const [sortTouched, setSortTouched] = useState(false);
  const [radiusMiles, setRadiusMiles] = useState("10");
  const [currentPage, setCurrentPage] = useState(1);
  const [comingSoon, setComingSoon] = useState(null); // { countryName, countryCode, message }
  const [searchFallbackNotice, setSearchFallbackNotice] = useState("");
  const isMobile = viewportWidth <= 768;
  const isTablet = viewportWidth > 768 && viewportWidth <= 1180;

  // Proximity search via Google Places Autocomplete
  const {
    inputValue: locationInput,
    suggestions: locationSuggestions,
    selectedCoords: userCoords,
    locationSource,
    geolocationStatus,
    showSuggestions,
    handleInputChange: handleLocationInputChange,
    selectSuggestion: handleSuggestionSelect,
    clearLocation,
    setShowSuggestions,
  } = useGooglePlacesAutocomplete();

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (geolocationStatus === "requesting") return;
    fetchFreelancers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geolocationStatus, userCoords, sortBy, sortTouched, radiusMiles]);

  useEffect(() => {
    fetchPublicSearchSettings();
  }, []);

  const normalizeRadiusOptions = (rawOptions) => {
    if (!Array.isArray(rawOptions)) {
      return [5, 10, 25, 50];
    }

    const normalized = rawOptions
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value >= 1 && value <= 200);

    const uniqueSorted = [...new Set(normalized)].sort((a, b) => a - b);
    return uniqueSorted.length > 0 ? uniqueSorted : [5, 10, 25, 50];
  };

  const fetchPublicSearchSettings = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/settings/public`,
        { cache: "no-store" },
      );

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const homepageSettings = data?.settings?.homepageSettings || {};
      const configuredOptions = normalizeRadiusOptions(
        homepageSettings.publicSearchRadiusOptions,
      );
      const configuredDefault = Number(
        homepageSettings.publicSearchDefaultRadiusMiles,
      );
      const fallbackDefault = configuredOptions.includes(10)
        ? 10
        : configuredOptions[0];
      const nextDefault = configuredOptions.includes(configuredDefault)
        ? configuredDefault
        : fallbackDefault;

      setRadiusOptions(configuredOptions);
      setRadiusMiles(String(nextDefault));
    } catch (err) {
      // Keep defaults when public settings are unavailable.
    }
  };

  const fetchFreelancers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (userCoords) {
        params.append("lat", userCoords.lat);
        params.append("lng", userCoords.lng);
        params.append("radius", radiusMiles);
        if (locationSource === "manual") {
          const parts = userCoords.label?.split(",").map((s) => s.trim());
          const searchCity = parts?.[0];
          const stateFromLabel = parts?.[1]?.split(" ")[0];
          if (searchCity) params.append("searchCity", searchCity);
          if (stateFromLabel) params.append("state", stateFromLabel);
        }
        if (sortTouched) {
          params.append("sort", sortBy);
          params.append("sortManual", "1");
        }
      }
      const qs = params.toString() ? `?${params.toString()}` : "";
      // Fetch all freelancers (R4 role)
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/staff/freelancers${qs}`,
        { cache: "no-store" },
      );

      if (response.ok) {
        const data = await response.json();

        // Check if user is from a country where OGESTYLE is not yet available
        if (data.comingSoon) {
          setSearchFallbackNotice("");
          setComingSoon({
            countryName: data.countryName,
            countryCode: data.userCountry,
            message: data.message,
          });
          return; // Don't show freelancers section
        }

        setFreelancers(data.freelancers || []);
        setSearchFallbackNotice(data.searchFallbackNotice || "");
      }
    } catch (err) {
      setSearchFallbackNotice("");
    } finally {
      setLoading(false);
    }
  };

  // Get unique professional types
  const professionalTypes = [
    "all",
    ...new Set(
      freelancers
        .map((f) => f.primaryProfessionalType || f.professionalType)
        .filter(Boolean),
    ),
  ];

  // Filter and sort freelancers
  const getFilteredFreelancers = () => {
    let filtered = freelancers.filter((freelancer) => {
      const matchesType =
        selectedType === "all" ||
        (freelancer.primaryProfessionalType || freelancer.professionalType) ===
          selectedType;

      return matchesType;
    });

    // Sort — skip if proximity mode (backend already sorted by distance)
    if (!userCoords) {
      if (sortBy === "name") {
        filtered.sort((a, b) => {
          const nameA = `${a.firstName} ${a.lastName}`;
          const nameB = `${b.firstName} ${b.lastName}`;
          return nameA.localeCompare(nameB);
        });
      } else if (sortBy === "rating") {
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      }
    }

    return filtered;
  };

  const filteredFreelancers = getFilteredFreelancers();
  const totalPages = isMobile
    ? Math.max(1, Math.ceil(filteredFreelancers.length / MOBILE_ITEMS_PER_PAGE))
    : 1;
  const paginatedFreelancers = isMobile
    ? filteredFreelancers.slice(
        (currentPage - 1) * MOBILE_ITEMS_PER_PAGE,
        currentPage * MOBILE_ITEMS_PER_PAGE,
      )
    : filteredFreelancers;

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedType, sortBy, sortTouched, radiusMiles, userCoords]);

  const usingBrowserLocation = locationSource === "browser";
  const usingManualLocation = locationSource === "manual";
  const displayedSortValue = userCoords && !sortTouched ? "distance" : sortBy;

  const getSortLabel = () => {
    switch (sortBy) {
      case "distance":
        return "Nearest";
      case "rating":
        return "Rating";
      case "name":
      default:
        return "A-Z";
    }
  };

  const handleSortChange = (e) => {
    if (e.target.value === "distance") {
      setSortTouched(false);
      return;
    }
    setSortBy(e.target.value);
    setSortTouched(true);
  };

  const handleLocationSelect = (prediction) => {
    setSortTouched(false);
    handleSuggestionSelect(prediction);
  };

  const handleClearLocation = () => {
    setSortTouched(false);
    clearLocation();
  };

  const handleFreelancerClick = (freelancer) => {
    const profileBasePath = freelancer.role === "R7" ? "/freelancers" : "/freelancer";
    if (freelancer.slug) {
      navigate(`${profileBasePath}/${freelancer.slug}`);
    } else {
      navigate(`${profileBasePath}/${freelancer._id}`);
    }
  };

  const getDisplayName = (freelancer) => {
    return freelancer.businessName || "Independent Professional";
  };

  return (
    <div
      style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
    >
      <CustomerHeader />

      {/* Country Availability Check */}
      {comingSoon ? (
        <ComingSoonPage
          countryName={comingSoon.countryName}
          countryCode={comingSoon.countryCode}
          source="freelancer_listings"
        />
      ) : (
        <>
          {/* Hero Section */}
          <div
            style={{
              background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
              color: "white",
              padding: isMobile
                ? "40px 20px"
                : isTablet
                  ? "48px 28px"
                  : "60px 40px",
              textAlign: "center",
            }}
          >
            <h1
              style={{
                fontSize: isMobile ? "24px" : "36px",
                marginBottom: "10px",
                fontWeight: "bold",
              }}
            >
              Find Independent Freelancers
            </h1>
            <p
              style={{
                fontSize: isMobile ? "14px" : "16px",
                opacity: 0.95,
                maxWidth: "800px",
                margin: "0 auto",
              }}
            >
              Browse talented independent beauty and wellness professionals
            </p>
          </div>

          {/* Main Content */}
          <div
            style={{
              flex: 1,
              maxWidth: isTablet ? "980px" : "1200px",
              width: "100%",
              margin: "0 auto",
              minHeight: isMobile ? "58vh" : "64vh",
              padding: isMobile ? "20px 20px 72px" : isTablet ? "28px 24px 88px" : "40px 20px 100px",
              boxSizing: "border-box",
            }}
          >
            {/* Search and Filters */}

            {/* Location proximity search */}
            {/* Search Row: location input + All Types + Sort */}
            <div
              style={{
                marginBottom: "20px",
                display: isMobile ? "grid" : "flex",
                gridTemplateColumns: isMobile ? "minmax(0, 1fr) minmax(0, 1fr)" : undefined,
                gap: "12px",
                alignItems: "flex-start",
                flexDirection: isMobile ? undefined : "row",
                flexWrap: isTablet ? "wrap" : "nowrap",
              }}
            >
            <div style={{ flex: 1, minWidth: 0, position: "relative", gridColumn: isMobile ? "1" : "auto", gridRow: isMobile ? "1" : "auto" }}>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", fontSize: "16px", pointerEvents: "none" }}>
                  📍
                </span>
                <input
                  type="text"
                  placeholder="Search near a city or address (e.g. Chicago, IL)..."
                  value={locationInput}
                  onChange={(e) => handleLocationInputChange(e.target.value)}
                  onFocus={() => locationSuggestions.length > 0 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  style={{
                    width: "100%",
                    padding: "12px 44px",
                    fontSize: "14px",
                    border: userCoords ? "1.5px solid #2d6da3" : "1px solid #d1d5db",
                    borderRadius: "8px",
                    background: userCoords ? "#eff6ff" : "white",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                {locationInput && (
                  <button
                    onClick={handleClearLocation}
                    style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: "16px", color: "#9ca3af" }}
                  >✕</button>
                )}
              </div>
              {showSuggestions && locationSuggestions.length > 0 && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100, background: "white", border: "1px solid #e5e7eb", borderRadius: "8px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", overflow: "hidden", marginTop: "4px" }}>
                  {locationSuggestions.map((pred) => (
                    <div
                      key={pred.place_id}
                      onMouseDown={() => handleLocationSelect(pred)}
                      style={{ padding: "12px 16px", cursor: "pointer", fontSize: "13px", color: "#374151", borderBottom: "1px solid #f3f4f6" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#f9fafb"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "white"}
                    >
                      📍 {pred.description}
                    </div>
                  ))}
                </div>
              )}
            </div>

              {/* All Types + Sort beside search */}
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                style={{
                  padding: "12px 16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "12px",
                  backgroundColor: "white",
                  outline: "none",
                  cursor: "pointer",
                  width: isMobile ? "100%" : isTablet ? "220px" : "180px",
                  flexShrink: 0,
                  minWidth: 0,
                  gridColumn: isMobile ? "1" : "auto",
                  gridRow: isMobile ? "2" : "auto",
                }}
              >
                <option value="all">All Types</option>
                {professionalTypes
                  .filter((t) => t !== "all")
                  .map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
              </select>

              <select
                value={displayedSortValue}
                onChange={handleSortChange}
                style={{
                  padding: "12px 16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "12px",
                  backgroundColor: "white",
                  outline: "none",
                  cursor: "pointer",
                  width: isMobile ? "100%" : isTablet ? "200px" : "160px",
                  flexShrink: 0,
                  minWidth: 0,
                  gridColumn: isMobile ? "2" : "auto",
                  gridRow: isMobile ? "2" : "auto",
                }}
              >
                <option value="distance" disabled={!userCoords}>Sort: Nearest</option>
                <option value="name">Sort: A-Z</option>
                <option value="rating">Sort: Rating</option>
              </select>

              <select
                value={radiusMiles}
                onChange={(e) => setRadiusMiles(e.target.value)}
                style={{
                  padding: "12px 16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "12px",
                  backgroundColor: "white",
                  outline: "none",
                  cursor: "pointer",
                  width: isMobile ? "100%" : isTablet ? "180px" : "140px",
                  flexShrink: 0,
                  minWidth: 0,
                  gridColumn: isMobile ? "2" : "auto",
                  gridRow: isMobile ? "1" : "auto",
                }}
              >
                {radiusOptions.map((radius) => (
                  <option key={radius} value={String(radius)}>
                    Distance: {radius} mi
                  </option>
                ))}
              </select>
            </div>

            {userCoords && (
              <div style={{ marginTop: "6px", marginBottom: "8px" }}>
                <p style={{ fontSize: "12px", color: "#2d6da3", marginTop: "0" }}>
                  {usingBrowserLocation ? (
                    <>✓ Showing freelancers closest to <strong>your location</strong></>
                  ) : (
                    <>✓ Showing freelancers closest to <strong>{userCoords.label}</strong></>
                  )}
                </p>
                <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                  {sortTouched
                    ? `Sorted by ${getSortLabel()} (manual override)`
                    : usingManualLocation
                      ? `Sorted by distance (${radiusMiles} mi): searched city first, then nearby cities`
                      : `Sorted by distance (${radiusMiles} mi): nearest freelancers first`}
                </p>
                {usingManualLocation && searchFallbackNotice && (
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#92400e",
                      background: "#fff7ed",
                      border: "1px solid #fed7aa",
                      borderRadius: "8px",
                      padding: "8px 10px",
                      marginTop: "8px",
                    }}
                  >
                    {searchFallbackNotice}
                  </p>
                )}
              </div>
            )}

            {!userCoords && (geolocationStatus === "denied" || geolocationStatus === "error" || geolocationStatus === "unavailable") && (
              <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "-4px", marginBottom: "16px" }}>
                Location access is off, so results are using the default listing order. Search by city to see nearby freelancers first.
              </p>
            )}

            {/* Type and Sort dropdowns moved above, beside the search field */}

            {/* Results Count */}
            <p
              style={{
                fontSize: "14px",
                color: "#6b7280",
                marginBottom: "20px",
              }}
            >
              Showing {filteredFreelancers.length} of {freelancers.length}{" "}
              freelancers
            </p>

            {/* Loading State */}
            {loading && (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "4px solid #f3f4f6",
                    borderTop: "4px solid #2d6da3",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto",
                  }}
                />
                <p style={{ marginTop: "20px", color: "#6b7280" }}>
                  Loading freelancers...
                </p>
              </div>
            )}

            {/* Empty State */}
            {!loading && filteredFreelancers.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  background: "#f9fafb",
                  borderRadius: "12px",
                  border: "1px solid #e5e7eb",
                }}
              >
                <p
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    marginBottom: "10px",
                  }}
                >
                  No freelancers found
                </p>
                <p style={{ color: "#6b7280", marginBottom: "20px" }}>
                  {selectedType !== "all"
                    ? "Try adjusting your search or filters"
                    : "Check back soon for talented independent professionals"}
                </p>
                {selectedType !== "all" && (
                  <button
                    onClick={() => {
                      setSelectedType("all");
                    }}
                    style={{
                      padding: "10px 20px",
                      background:
                        "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}

            {/* Freelancer Cards Grid */}
            {!loading && filteredFreelancers.length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "repeat(3, 1fr)"
                    : isTablet
                      ? "repeat(auto-fit, minmax(170px, 1fr))"
                      : "repeat(auto-fill, 180px)",
                  gap: isMobile ? "0px 6px" : isTablet ? "14px 16px" : "12px",
                  alignItems: "start",
                }}
              >
                {paginatedFreelancers.map((freelancer) => {
                  const displayName = getDisplayName(freelancer);
                  const rating = freelancer.rating || 0;
                  const professionalType =
                    freelancer.primaryProfessionalType ||
                    freelancer.professionalType ||
                    null;
                  const location =
                    (freelancer.city || freelancer.activeLocationId?.city) &&
                    (freelancer.state || freelancer.activeLocationId?.state)
                      ? `${freelancer.city || freelancer.activeLocationId?.city}, ${freelancer.state || freelancer.activeLocationId?.state}`
                      : freelancer.city ||
                        freelancer.activeLocationId?.city ||
                        null;
                  const distanceLabel = freelancer.distanceLabel || null;

                  return (
                    <div
                      key={freelancer._id}
                      onClick={() => handleFreelancerClick(freelancer)}
                      style={{
                        background: "transparent",
                        borderRadius: "12px",
                        overflow: "visible",
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                        width: isMobile ? "100%" : isTablet ? "100%" : "180px",
                        // minHeight removed to allow natural height
                        height: "auto",
                        display: "flex",
                        flexDirection: "column",
                        boxSizing: "border-box",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-4px)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <div
                        style={{
                          height: "126px",
                          width: "100%",
                          backgroundColor: "transparent",
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "center",
                          position: "relative",
                          paddingBottom: "2px",
                        }}
                      >
                        <div
                          style={{
                            width: isMobile ? "88px" : isTablet ? "96px" : "112px",
                            height: isMobile ? "88px" : isTablet ? "96px" : "112px",
                            borderRadius: "999px",
                            background: freelancer.brandImage
                              ? `url("${freelancer.brandImage}") center/cover no-repeat`
                              : `linear-gradient(135deg, ${["#2d6da3", "#f093fb", "#4facfe", "#43e97b", "#fa709a"][freelancer._id.charCodeAt(0) % 5]} 0%, ${["#4a90c6", "#f5576c", "#00f2fe", "#38f9d7", "#fee140"][freelancer._id.charCodeAt(0) % 5]} 100%)`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "30px",
                            fontWeight: "700",
                            boxShadow: "0 8px 16px rgba(0,0,0,0.14)",
                          }}
                        >
                          {!freelancer.brandImage &&
                            (freelancer.firstName?.[0] || "?")}
                        </div>
                      </div>

                      <div
                        style={{
                          padding: "3px 8px 2px 8px",
                          textAlign: "center",
                        }}
                      >
                        <h3
                          style={{
                            fontSize: isMobile ? "12px" : "15px",
                            fontWeight: "600",
                            color: "#111827",
                            marginTop: "0",
                            marginBottom: "2px",
                            lineHeight: "1.15",
                            overflow: "visible",
                            textOverflow: "clip",
                            whiteSpace: "normal",
                          }}
                        >
                          {displayName}
                        </h3>

                        {location && (
                          <p
                            style={{
                              fontSize: isMobile ? "10px" : "12px",
                              color: "#6b7280",
                              marginTop: "0",
                              marginBottom: "2px",
                              lineHeight: "1.2",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {location}
                          </p>
                        )}

                        {professionalType && (
                          <p
                            style={{
                              fontSize: isMobile ? "10px" : "12px",
                              color: "#1a659e",
                              marginTop: "0",
                              marginBottom: "2px",
                              fontWeight: "500",
                              lineHeight: "1.2",
                              overflow: "visible",
                              textOverflow: "clip",
                              whiteSpace: "normal",
                            }}
                          >
                            {professionalType.charAt(0).toUpperCase() +
                              professionalType.slice(1)}
                          </p>
                        )}

                        {rating > 0 && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "3px",
                              lineHeight: "0.95",
                            }}
                          >
                            <span
                              style={{
                                color: "#f59e0b",
                                fontSize: isMobile ? "10px" : "13px",
                              }}
                            >
                              ★★★★★
                            </span>
                            <span
                              style={{
                                fontSize: isMobile ? "10px" : "13px",
                                color: "#6b7280",
                              }}
                            >
                              ({rating.toFixed(1)})
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!loading && isMobile && filteredFreelancers.length > 0 && totalPages > 1 && (
              <div
                style={{
                  marginTop: "14px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  style={{
                    padding: "6px 10px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    background: currentPage === 1 ? "#f3f4f6" : "white",
                    color: currentPage === 1 ? "#9ca3af" : "#111827",
                    fontSize: "11px",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  Prev
                </button>
                <span style={{ fontSize: "11px", color: "#6b7280" }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  style={{
                    padding: "6px 10px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    background:
                      currentPage === totalPages ? "#f3f4f6" : "white",
                    color:
                      currentPage === totalPages ? "#9ca3af" : "#111827",
                    fontSize: "11px",
                    cursor:
                      currentPage === totalPages ? "not-allowed" : "pointer",
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}

      <CustomerFooter />
    </div>
  );
}

export default FindFreelancers;
